/**
 * ProjectContext — which project the UI is acting in.
 *
 * ── The one thing that must not go wrong ──
 * The active project is BOTH React state (so the UI re-renders) and a module variable in apiClient (so
 * the `X-Stride-Project` header goes out on every request, including from services that live outside
 * the component tree). Those two must never disagree: a switcher that updated the label without
 * updating the header would show one project's name above another project's data — which is the worst
 * possible failure for a tenancy feature, because it looks like it works.
 *
 * So switching goes through ONE function that sets both, and the initial value is read back from
 * apiClient rather than kept independently.
 *
 * ── Why a stale stored project is not trusted ──
 * The last-used project is remembered across reloads, but it is validated against the projects the
 * SERVER says this person can see. Someone removed from a project would otherwise keep sending its id
 * and get 404s on every screen with no explanation — and a removed member trying to reach a project
 * they no longer belong to should land somewhere useful, not at a wall.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react'
import type { ProjectSummary } from '@stride/shared'
import { useSession } from './SessionContext'
import { getActiveProject, setActiveProject, initActiveProject } from '../lib/apiClient'

export interface ProjectContextValue {
  /** Projects this person can see, from the server. */
  projects: ProjectSummary[]
  /** The one being acted in, or null before any is resolved. */
  active: ProjectSummary | null
  /** Switch. Reloads the page: every screen's data belongs to the previous project. */
  switchTo: (id: string) => void
  /** Re-ask the server — after creating a project, or being granted access to one. */
  refresh: () => void
  /** True when the caller reaches the active project as platform admin rather than as a member. */
  viaPlatformAdmin: boolean
}

const Ctx = createContext<ProjectContextValue>({
  projects: [], active: null, switchTo: () => {}, refresh: () => {}, viaPlatformAdmin: false,
})

export function ProjectProvider({ children }: { children: ReactNode }): JSX.Element {
  const { me, refresh: refreshSession } = useSession()
  const projects = me?.projects ?? []

  // Initialised from apiClient so the two cannot start out disagreeing.
  const [activeId, setActiveId] = useState<string | null>(() => getActiveProject() ?? initActiveProject())

  // Reconcile the remembered project against what the server actually allows.
  useEffect(() => {
    if (projects.length === 0) return
    const stillVisible = activeId !== null && projects.some((p) => p.id === activeId)
    if (stillVisible) return
    // Either nothing was remembered, or the remembered project is no longer visible (access revoked,
    // or the project was deleted). Fall back to the first rather than leaving a dangling id that
    // would 404 every screen.
    const fallback = projects[0]
    if (fallback !== undefined) {
      setActiveProject(fallback.id)
      setActiveId(fallback.id)
    }
  }, [projects, activeId])

  const switchTo = useCallback((id: string) => {
    if (id === activeId) return
    setActiveProject(id)
    setActiveId(id)
    // A full reload, deliberately.
    //
    // Every cached query, every open panel and every id held in a component belongs to the project
    // being left. Invalidating that selectively would mean auditing every screen and getting it right
    // forever; reloading is one line and cannot be partially wrong. Switching project is rare and
    // deliberate, so the cost is acceptable.
    window.location.assign('/')
  }, [activeId])

  const active = useMemo(
    () => projects.find((p) => p.id === activeId) ?? null,
    [projects, activeId],
  )

  const value: ProjectContextValue = {
    projects,
    active,
    switchTo,
    refresh: refreshSession,
    viaPlatformAdmin: active?.via === 'platform-admin',
  }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useProject(): ProjectContextValue {
  return useContext(Ctx)
}
